import { useEffect, useRef, useState } from 'react'
import api from '../../utils/apiClient'

const STATE_CODE = {
	Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR', California: 'CA',
	Colorado: 'CO', Connecticut: 'CT', Delaware: 'DE', Florida: 'FL', Georgia: 'GA',
	Hawaii: 'HI', Idaho: 'ID', Illinois: 'IL', Indiana: 'IN', Iowa: 'IA',
	Kansas: 'KS', Kentucky: 'KY', Louisiana: 'LA', Maine: 'ME', Maryland: 'MD',
	Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN', Mississippi: 'MS',
	Missouri: 'MO', Montana: 'MT', Nebraska: 'NE', Nevada: 'NV', 'New Hampshire': 'NH',
	'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC',
	'North Dakota': 'ND', Ohio: 'OH', Oklahoma: 'OK', Oregon: 'OR', Pennsylvania: 'PA',
	'Rhode Island': 'RI', 'South Carolina': 'SC', 'South Dakota': 'SD', Tennessee: 'TN',
	Texas: 'TX', Utah: 'UT', Vermont: 'VT', Virginia: 'VA', Washington: 'WA',
	'West Virginia': 'WV', Wisconsin: 'WI', Wyoming: 'WY', 'District of Columbia': 'DC',
}

const cache = new Map()
const MAX_CACHE = 50

const cacheGet = key => cache.get(key)
const cacheSet = (key, value) => {
	cache.set(key, value)
	if (cache.size > MAX_CACHE) {
		const oldestKey = cache.keys().next().value
		cache.delete(oldestKey)
	}
}

const normalize = items =>
	items
		.map((item, idx) => {
			const stateName = item.address?.state || ''
			const stateCode = STATE_CODE[stateName] || stateName.slice(0, 2).toUpperCase()
			const city = item.address?.city || ''
			return {
				id: `${item.lat},${item.lon}-${idx}`,
				label: item.display_name,
				city,
				state: stateName,
				stateCode,
				lat: item.lat,
				lon: item.lon,
				postcode: item.address?.postcode,
			}
		})
		.filter(s => s.city)

export default function useGeocodeSuggest(
	query,
	{ minChars = 2, debounceMs = 400, enabled = true } = {}
) {
	const [suggestions, setSuggestions] = useState([])
	const [loading, setLoading] = useState(false)
	const abortRef = useRef(null)

	useEffect(() => {
		// Caller (LocationField) toggles `enabled` when the dropdown opens.
		// Prevents refetching saved tab values on page mount when the
		// dropdown isn't even being shown.
		if (!enabled) {
			setSuggestions([])
			setLoading(false)
			return
		}
		const trimmed = (query || '').trim()
		if (trimmed.length < minChars) {
			setSuggestions([])
			setLoading(false)
			return
		}

		const cacheKey = trimmed.toLowerCase()
		const cached = cacheGet(cacheKey)
		if (cached) {
			setSuggestions(cached)
			setLoading(false)
			return
		}

		const timer = setTimeout(async () => {
			if (abortRef.current) abortRef.current.abort()
			const controller = new AbortController()
			abortRef.current = controller
			setLoading(true)
			try {
				const res = await api.post(
					'/api/geocode/search',
					{ query: trimmed },
					{ signal: controller.signal }
				)
				const items = res.data?.data || []
				const normalized = normalize(items)
				cacheSet(cacheKey, normalized)
				setSuggestions(normalized)
			} catch (err) {
				if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
					setSuggestions([])
				}
			} finally {
				setLoading(false)
			}
		}, debounceMs)

		return () => {
			clearTimeout(timer)
			if (abortRef.current) abortRef.current.abort()
		}
	}, [query, minChars, debounceMs, enabled])

	return { suggestions, loading }
}
